<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { debounce, format } from 'quasar'

import { getI18n } from '@/plugins/i18n'
import { isoRangeForPreset, presetOrCustom, savedPreset } from '@/composables/date-range-presets'
import { useDetectDevice } from '@/composables/device'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { formatDuration, isReportRowModified, reportPeriodLabel } from '@/composables/report'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore, infoBoxIdEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import CompanyCombobox from '@/components/shared/CompanyCombobox.vue'
import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import DocumentLanguageSelect from '@/components/shared/DocumentLanguageSelect.vue'
import ReportRowsEditor from '@/components/accounting/ReportRowsEditor.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  prefill: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  startStep: {
    type: Number,
    required: false,
    default: 0,
  },
})

const emit = defineEmits(['update', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const { reportSubjectEnum, reportTimeBasisEnum, reportRowKindEnum, reportScopeEnum } = accountingStore
const { dateRangePresetEnum } = portalStore

const STEP = Object.freeze({ SUBJECT_KIND: 0, ROWS: 1, DATA: 2 })
const ALLOWED_PRESETS = Object.freeze([dateRangePresetEnum.WEEK, dateRangePresetEnum.MONTH, dateRangePresetEnum.QUARTER, dateRangePresetEnum.SEMESTER, dateRangePresetEnum.YEAR])

const isEdit = !!props.modelValue.id
const { step } = useStepper({ step: isEdit ? STEP.DATA : props.startStep })

const onCancel = () => emit('cancel')
useNavigator(onCancel)

const monthRange = isoRangeForPreset(dateRangePresetEnum.MONTH)
const defaults = {
  name: '',
  subject: reportSubjectEnum.STAFF,
  staff: null,
  company: null,
  listing: null,
  period_preset: dateRangePresetEnum.MONTH,
  date_from: monthRange.from,
  date_to: monthRange.to,
  completed_projects_only: false,
  excluded_project_ids: [],
  time_basis: reportTimeBasisEnum.WORK,
  include_comments: true,
  language: portalStore.selectedLanguage,
}
const editItem = ref(isEdit
  ? { ...Object.fromEntries(Object.keys(defaults).map(field => [field, props.modelValue[field] ?? defaults[field]])), period_preset: presetOrCustom(props.modelValue.period_preset) }
  : { ...defaults, ...props.prefill, period_preset: presetOrCustom('period_preset' in props.prefill ? props.prefill.period_preset : defaults.period_preset) })
const { isUpdated } = useIsUpdated(editItem)
const scope = ref(editItem.value.listing ? reportScopeEnum.LISTING : reportScopeEnum.COMPANY)

const rows = ref([])
const staffCosts = ref([])
const rowsChanged = ref(false)
const isComputing = ref(false)
const candidateProjects = ref([])
const isLoadingProjects = ref(false)
const isSaving = ref(false)
const computedOnce = ref(false)

planningStore.fetchStaffsAsync({}, true)
planningStore.fetchCompaniesAsync({}, true)
planningStore.fetchListingsAsync({ all: true }, true)
accountingStore.fetchCompanyDataAsync({}, true)

const reportSubjects = accountingStore.getLocalizedReportSubjects()
const isStaffReport = computed(() => accountingStore.isStaffSubject(editItem.value.subject))
const subjectMissing = computed(() => (isStaffReport.value ? !editItem.value.staff : !editItem.value.company && !editItem.value.listing))
const listingOptions = computed(() => planningStore.listings.filter(listing => !listing.is_generic && !listing.archived))
const staffOptions = computed(() => planningStore.staff.map(staff => ({ id: staff.id, label: planningStore.getServiceName(staff) })))
const subjectName = computed(() => {
  if (isStaffReport.value) return staffOptions.value.find(option => option.id === editItem.value.staff)?.label || ''
  if (editItem.value.listing) return planningStore.listings.find(listing => listing.id === editItem.value.listing)?.name || ''
  return planningStore.companies.find(company => company.id === editItem.value.company)?.name || ''
})
const subjectKindName = computed(() => reportSubjects.find(item => item.id === editItem.value.subject)?.name || '')
const periodRange = computed(() => ({ from: editItem.value.date_from, to: editItem.value.date_to }))
const activeTimeRows = computed(() => rows.value.filter(row => row.kind === reportRowKindEnum.TIME && !row.is_excluded))
const rowsSummary = computed(() => i18n.t('common.report.rowsSummary', {
  count: rows.value.length,
  net: formatDuration(activeTimeRows.value.reduce((t, r) => t + (r.net_minutes || 0), 0)),
  gross: formatDuration(activeTimeRows.value.reduce((t, r) => t + (r.gross_minutes || 0), 0)),
}))
const rowsStepCaption = computed(() => [subjectName.value, reportPeriodLabel(editItem.value), computedOnce.value ? rowsSummary.value : ''].filter(Boolean).join(' · '))
const editedRowsCount = computed(() => rows.value.filter(isReportRowModified).length)
const projectStatuses = planningStore.getLocalizedProjectStatuses()
const excludedProjects = computed(() => new Set(editItem.value.excluded_project_ids))
const selectedProjectsCount = computed(() => candidateProjects.value.filter(project => !excludedProjects.value.has(project.id)).length)
const allProjectsSelected = computed(() => {
  if (!selectedProjectsCount.value) return false
  return selectedProjectsCount.value === candidateProjects.value.length ? true : null
})
const noProjectSelected = computed(() => !isStaffReport.value && candidateProjects.value.length > 0 && !selectedProjectsCount.value)
const hasValidFilters = computed(() => !subjectMissing.value && !noProjectSelected.value)

const subjectPlainName = computed(() => {
  if (!isStaffReport.value) return subjectName.value
  const staff = planningStore.staff.find(item => item.id === editItem.value.staff)
  return [staff?.name, staff?.first_name].filter(Boolean).join(' ')
})
const periodName = () => (editItem.value.period_preset === dateRangePresetEnum.MONTH
  ? format.capitalize(new Date(editItem.value.date_from).toLocaleDateString(portalStore.selectedLanguage, { month: 'long', year: 'numeric' }))
  : reportPeriodLabel({ date_from: editItem.value.date_from, date_to: editItem.value.date_to }))
const defaultName = () => [subjectPlainName.value, periodName()].filter(Boolean).join(' · ')

const params = () => ({
  subject: editItem.value.subject,
  staff: isStaffReport.value ? editItem.value.staff : null,
  company: isStaffReport.value || scope.value === reportScopeEnum.LISTING ? null : editItem.value.company,
  listing: !isStaffReport.value && scope.value === reportScopeEnum.LISTING ? editItem.value.listing : null,
  date_from: editItem.value.date_from,
  date_to: editItem.value.date_to,
  completed_projects_only: editItem.value.completed_projects_only,
  excluded_project_ids: isStaffReport.value ? [] : editItem.value.excluded_project_ids,
  time_basis: editItem.value.time_basis,
  include_comments: editItem.value.include_comments,
})

const computeSignature = (staffCostValues) => JSON.stringify({ ...params(), staff_costs: staffCostValues })
let latestComputeRequest = 0
let lastComputedSignature = null
const computeAsync = async() => {
  const request = ++latestComputeRequest
  isComputing.value = true
  try {
    const result = await accountingStore.computeReportAsync({ ...params(), staff_costs: staffCosts.value, rows: rows.value })
    if (request !== latestComputeRequest) return
    rows.value = result.rows
    staffCosts.value = result.staff_costs
    computedOnce.value = true
    lastComputedSignature = computeSignature(result.staff_costs)
  } finally {
    if (request === latestComputeRequest) isComputing.value = false
  }
}
const computeAfterEditing = debounce(computeAsync, webSiteStore.getInputDebounce())
const computeKey = computed(() => (hasValidFilters.value ? computeSignature(staffCosts.value) : null))
watch(computeKey, (key) => {
  if (key && key !== lastComputedSignature) computeAfterEditing()
})

let latestProjectsRequest = 0
const fetchCandidateProjectsAsync = async() => {
  const request = ++latestProjectsRequest
  isLoadingProjects.value = true
  try {
    const projects = await accountingStore.fetchReportProjectsAsync({ ...params(), excluded_project_ids: [] })
    if (request !== latestProjectsRequest) return
    candidateProjects.value = projects
    const candidateIds = new Set(projects.map(project => project.id))
    editItem.value.excluded_project_ids = editItem.value.excluded_project_ids.filter(id => candidateIds.has(id))
  } finally {
    if (request === latestProjectsRequest) isLoadingProjects.value = false
  }
}
const projectScopeKey = computed(() => (isStaffReport.value || subjectMissing.value ? null : JSON.stringify([params().company, params().listing, editItem.value.date_from, editItem.value.date_to, editItem.value.completed_projects_only])))
watch(projectScopeKey, (key) => {
  if (key) fetchCandidateProjectsAsync()
  else candidateProjects.value = []
}, { immediate: true })

const onSubjectKindSelected = (subject) => {
  editItem.value.subject = subject
  if (accountingStore.isStaffSubject(subject)) {
    editItem.value.company = null
    editItem.value.listing = null
  } else editItem.value.staff = null
}
const onScopeSelected = (value) => {
  scope.value = value
  if (value === reportScopeEnum.LISTING) editItem.value.company = null
  else editItem.value.listing = null
}
const onRangeChanged = (range) => {
  editItem.value.date_from = range.from
  editItem.value.date_to = range.to
}
const onProjectToggled = (projectId, selected) => {
  const excluded = editItem.value.excluded_project_ids.filter(id => id !== projectId)
  editItem.value.excluded_project_ids = selected ? excluded : [...excluded, projectId]
}
const onAllProjectsToggled = (selected) => {
  editItem.value.excluded_project_ids = selected ? [] : candidateProjects.value.map(project => project.id)
}
const onRowsExclusionChanged = (newRows) => {
  rows.value = newRows
  rowsChanged.value = true
}
const onRowsChanged = (newRows) => {
  onRowsExclusionChanged(newRows)
  computeAfterEditing()
}

const goTo = async(target) => {
  if (target === STEP.DATA && computeKey.value && computeKey.value !== lastComputedSignature) await computeAsync()
  if (target === STEP.DATA && !editItem.value.name) editItem.value.name = defaultName()
  step.value = target
}

const draftOf = (row) => ({
  key: row.key,
  original: row.original,
  ...Object.fromEntries(Object.keys(row.original || {}).map(field => [field, row[field]])),
})

const onSaveAsync = async() => {
  isSaving.value = true
  const payload = {
    ...(isEdit ? { id: props.modelValue.id } : {}),
    ...params(),
    name: editItem.value.name || defaultName(),
    period_preset: savedPreset(editItem.value.period_preset),
    language: editItem.value.language,
    rows: rows.value.map(draftOf),
    staff_costs: staffCosts.value,
  }
  const response = await accountingStore.upsertReportAsync(payload).finally(() => { isSaving.value = false })
  emit('update', response.data)
}

onMounted(async() => {
  if (!isEdit) {
    if (computeKey.value) computeAsync()
    return
  }
  const stored = await accountingStore.fetchReportRowsAsync(props.modelValue.id)
  rows.value = stored.rows
  staffCosts.value = stored.staff_costs
  lastComputedSignature = computeSignature(stored.staff_costs)
  computedOnce.value = true
})
</script>

<template>
  <q-card class="dialog-card-flex">
    <navigate-back
      :text="isEdit ? `${i18n.t('common.edit')} · ${props.modelValue.name}` : i18n.t('common.report.generate')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content">
      <q-card-section class="q-px-sm">
        <q-stepper
          :model-value="step"
          class="q-py-none"
          vertical
          animated
          header-nav
          @update:model-value="goTo"
        >
          <q-step
            :name="STEP.SUBJECT_KIND"
            :title="i18n.t('common.report.subjectKind')"
            :caption="step > STEP.SUBJECT_KIND ? subjectKindName : null"
            icon="mdi-shape-outline"
            :done="isEdit || step > STEP.SUBJECT_KIND"
          >
            <div class="row q-col-gutter-sm">
              <div v-for="item in reportSubjects" :key="item.id" :class="isMobile ? 'col-12' : 'col-6'">
                <q-item
                  clickable
                  bordered
                  class="report-type-card"
                  :class="{ 'report-type-card--active': editItem.subject === item.id }"
                  @click="onSubjectKindSelected(item.id)"
                >
                  <q-item-section avatar>
                    <q-avatar
                      :icon="item.icon"
                      :color="editItem.subject === item.id ? 'primary' : 'grey-3'"
                      :text-color="editItem.subject === item.id ? 'white' : 'grey-8'"
                      size="md"
                      rounded
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-medium">{{ item.name }}</q-item-label>
                    <q-item-label caption>{{ item.description }}</q-item-label>
                  </q-item-section>
                </q-item>
              </div>
            </div>
            <info-box :id="infoBoxIdEnum.REPORT_LAYOUT_AT_PRINT" class="q-mt-md" :message="i18n.t('common.report.layoutAtPrintInfo')" />
            <stepper-navigation :continue="() => goTo(STEP.ROWS)" />
          </q-step>

          <q-step
            :name="STEP.ROWS"
            :title="i18n.t('common.report.steps.filtersAndRows')"
            :caption="isEdit || step > STEP.ROWS ? rowsStepCaption : null"
            icon="mdi-table-filter"
            :done="isEdit || step > STEP.ROWS"
          >
            <div class="row q-col-gutter-sm">
              <q-select
                v-if="isStaffReport"
                v-model="editItem.staff"
                :class="isMobile ? 'col-12' : 'col-4'"
                :label="i18n.t('common.staff')"
                :options="staffOptions"
                option-value="id"
                option-label="label"
                emit-value
                map-options
                hide-bottom-space
                :rules="[val => !!val || i18n.t('common.required')]"
              />
              <template v-else>
                <q-toggle
                  :model-value="scope"
                  class="self-center"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :true-value="reportScopeEnum.LISTING"
                  :false-value="reportScopeEnum.COMPANY"
                  :label="i18n.t('common.report.byConvention')"
                  @update:model-value="onScopeSelected"
                />
                <company-combobox v-if="scope === reportScopeEnum.COMPANY" v-model="editItem.company" :class="isMobile ? 'col-12' : 'col-4'" />
                <q-select
                  v-else
                  v-model="editItem.listing"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.report.convention')"
                  :options="listingOptions"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  hide-bottom-space
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
              </template>
              <date-range-navigator
                class="report-period self-center"
                :class="isMobile ? 'col-12' : isStaffReport ? 'col-8' : 'col-5'"
                :model-value="periodRange"
                :default-preset="editItem.period_preset ?? dateRangePresetEnum.MONTH"
                :allowed-presets="ALLOWED_PRESETS"
                @update:model-value="onRangeChanged"
                @preset="value => { editItem.period_preset = value }"
              />
              <template v-if="!isStaffReport">
                <form-section-title :label="i18n.t('common.report.projects')" />
                <q-toggle v-model="editItem.completed_projects_only" class="col-12" :label="i18n.t('common.report.completedProjectsOnly')" />
                <div class="col-12">
                  <q-list bordered separator class="rounded-borders report-projects-list">
                    <q-item v-if="candidateProjects.length" dense>
                      <q-item-section side>
                        <q-checkbox :model-value="allProjectsSelected" @update:model-value="onAllProjectsToggled" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="text-weight-medium">{{ i18n.t('common.selectAll') }}</q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-item-label caption>{{ i18n.t('common.report.projectsSelected', { selected: selectedProjectsCount, total: candidateProjects.length }) }}</q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-for="project in candidateProjects"
                      :key="project.id"
                      v-ripple
                      tag="label"
                      dense
                      clickable
                    >
                      <q-item-section side>
                        <q-checkbox :model-value="!excludedProjects.has(project.id)" @update:model-value="selected => onProjectToggled(project.id, selected)" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>{{ project.name }}</q-item-label>
                        <q-item-label caption>{{ [project.client_name, project.start && `${formatShortDate(project.start)} – ${formatShortDate(project.end)}`].filter(Boolean).join(' · ') }}</q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <chip-template :values="[projectStatuses[project.status]]" />
                      </q-item-section>
                    </q-item>
                    <q-item v-if="!candidateProjects.length && !isLoadingProjects">
                      <q-item-section class="text-grey-6">{{ i18n.t('common.report.noProjectsInPeriod') }}</q-item-section>
                    </q-item>
                    <q-inner-loading :showing="isLoadingProjects" />
                  </q-list>
                </div>
              </template>
              <form-section-title :label="i18n.t('common.report.steps.options')" />
              <div class="row items-center no-wrap" :class="isMobile ? 'col-12' : 'col-6'">
                <q-toggle
                  v-model="editItem.time_basis"
                  :true-value="reportTimeBasisEnum.REPORTED"
                  :false-value="reportTimeBasisEnum.WORK"
                  :label="i18n.t('common.report.useReportedTimes')"
                />
                <info-box popup class="q-ml-xs" :message="i18n.t('common.report.timeBasisHint')" />
              </div>
              <q-toggle v-model="editItem.include_comments" :class="isMobile ? 'col-12' : 'col-6'" :label="i18n.t('common.report.includeComments')" />
              <template v-if="hasValidFilters">
                <form-section-title :label="i18n.t('common.report.steps.rows')" />
                <div class="col-12 relative-position">
                  <report-rows-editor
                    :rows="rows"
                    :subject="editItem.subject"
                    @change="onRowsChanged"
                    @exclusion-change="onRowsExclusionChanged"
                  />
                  <q-inner-loading :showing="isComputing" />
                </div>
              </template>
            </div>
            <info-box
              v-if="!isStaffReport"
              :id="infoBoxIdEnum.REPORT_PROJECT_INCLUSION"
              class="q-mt-md"
              :message="i18n.t('common.report.projectInclusionInfo')"
            />
            <info-box
              v-if="!isStaffReport"
              :id="infoBoxIdEnum.REPORT_CONVENTION_SCOPE"
              class="q-mt-md"
              :message="i18n.t('common.report.conventionScopeInfo')"
            />
            <stepper-navigation
              :back="() => goTo(STEP.SUBJECT_KIND)"
              :continue="() => goTo(STEP.DATA)"
              :loading="isComputing"
              :disable="!hasValidFilters || !computedOnce"
            />
          </q-step>

          <q-step
            :name="STEP.DATA"
            :title="i18n.t('common.report.steps.data')"
            icon="mdi-file-chart-outline"
            :done="isEdit"
            :header-nav="isEdit || (hasValidFilters && computedOnce)"
          >
            <div class="row q-col-gutter-sm">
              <q-input
                v-model="editItem.name"
                :class="isMobile ? 'col-12' : 'col-8'"
                :label="i18n.t('common.name')"
                :rules="[val => !!(val && val.trim()) || i18n.t('common.required')]"
              />
              <document-language-select v-model="editItem.language" :class="isMobile ? 'col-12' : 'col-4'" />
            </div>
            <q-list dense class="q-mt-sm">
              <q-item class="q-px-none"><q-item-section><q-item-label caption>{{ i18n.t('common.report.subject') }}</q-item-label><q-item-label>{{ subjectName }}</q-item-label></q-item-section></q-item>
              <q-item class="q-px-none"><q-item-section><q-item-label caption>{{ i18n.t('common.report.period') }}</q-item-label><q-item-label>{{ reportPeriodLabel(editItem) }}</q-item-label></q-item-section></q-item>
              <q-item class="q-px-none"><q-item-section><q-item-label caption>{{ i18n.t('common.report.totals') }}</q-item-label><q-item-label>{{ rowsSummary }}</q-item-label></q-item-section></q-item>
              <q-item v-if="editedRowsCount" class="q-px-none"><q-item-section><q-item-label caption>{{ i18n.t('common.report.adjustments') }}</q-item-label><q-item-label>{{ editedRowsCount }}</q-item-label></q-item-section></q-item>
            </q-list>
            <info-box :id="infoBoxIdEnum.REPORT_DOCUMENT_LANGUAGE" class="q-mt-md" :message="i18n.t('common.report.documentLanguageInfo')" />
            <stepper-navigation
              :back="() => goTo(STEP.ROWS)"
              :finish="isEdit ? null : onSaveAsync"
              :finish-label="i18n.t('common.report.generate')"
              :loading="isSaving"
              :disable="!editItem.name"
            />
          </q-step>
        </q-stepper>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="isEdit && (isUpdated || rowsChanged)" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="positive"
        :label="i18n.t('common.update')"
        :loading="isSaving"
        @click="onSaveAsync"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped lang="scss">
.report-type-card {
  border: 1px solid $grey-4;
  border-radius: $generic-border-radius;
}

.report-type-card--active {
  border-color: $primary;
  background-color: color-mix(in srgb, var(--q-primary) 6%, transparent);
}
.report-period :deep(.date-range-navigator__input) {
  flex: 1 1 auto;
}

.report-projects-list {
  position: relative;
  max-height: 40vh;
  overflow-y: auto;
}
</style>
